import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Users, CheckCircle, XCircle, Clock, TrendingUp, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/admin/stats`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [token]);

  const statusConfig = {
    'New': { color: 'bg-blue-100 text-blue-700', icon: FileText },
    'Under Review': { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
    'Accepted': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
    'Rejected': { color: 'bg-red-100 text-red-700', icon: XCircle },
  };

  const departmentNames = {
    'health-allied-sciences': 'Health & Allied Sciences',
    'business': 'Business',
    'hotel-tourism': 'Hotel & Tourism',
    'science-technology': 'Science & Technology',
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#1e3a5f]">Dashboard</h1>
          <p className="text-[#475569]">Overview of application statistics</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-none shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#475569] text-sm">Total Applications</p>
                  <p className="font-display text-3xl font-bold text-[#1e3a5f]">
                    {stats?.totalApplications || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-[#1e3a5f]/10 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#1e3a5f]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {Object.entries(stats?.byStatus || {}).map(([status, count]) => {
            const config = statusConfig[status];
            const Icon = config?.icon || FileText;
            return (
              <Card key={status} className="border-none shadow-md">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[#475569] text-sm">{status}</p>
                      <p className="font-display text-3xl font-bold text-[#1e3a5f]">{count}</p>
                    </div>
                    <div className={`w-12 h-12 ${config?.color || 'bg-gray-100'} rounded-xl flex items-center justify-center`}>
                      <Icon className="w-6 h-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Department & Intake Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-none shadow-md">
            <CardHeader>
              <CardTitle className="text-[#1e3a5f]">Applications by Department</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(stats?.byDepartment || {}).map(([dept, count]) => (
                  <div key={dept} className="flex items-center justify-between">
                    <span className="text-[#475569]">{departmentNames[dept] || dept}</span>
                    <Badge className="bg-[#1e3a5f] text-white">{count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-md">
            <CardHeader>
              <CardTitle className="text-[#1e3a5f]">Applications by Intake</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(stats?.byIntake || {}).map(([intake, count]) => (
                  <div key={intake} className="flex items-center justify-between">
                    <span className="text-[#475569]">{intake} Intake</span>
                    <Badge className="bg-[#d4a855] text-[#1e3a5f]">{count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Applications */}
        <Card className="border-none shadow-md">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-[#1e3a5f]">Recent Applications</CardTitle>
            <Link to="/admin/applications" className="text-[#2563eb] text-sm flex items-center hover:underline">
              View All <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </CardHeader>
          <CardContent>
            {stats?.recentApplications?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#e2e8f0]">
                      <th className="text-left py-3 px-4 text-[#475569] font-medium text-sm">Application #</th>
                      <th className="text-left py-3 px-4 text-[#475569] font-medium text-sm">Name</th>
                      <th className="text-left py-3 px-4 text-[#475569] font-medium text-sm">Programme</th>
                      <th className="text-left py-3 px-4 text-[#475569] font-medium text-sm">Intake</th>
                      <th className="text-left py-3 px-4 text-[#475569] font-medium text-sm">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recentApplications.map((app) => (
                      <tr key={app.id} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc]">
                        <td className="py-3 px-4">
                          <Link to={`/admin/applications/${app.id}`} className="text-[#2563eb] hover:underline">
                            {app.applicationNumber}
                          </Link>
                        </td>
                        <td className="py-3 px-4 text-[#1e3a5f]">{app.fullName}</td>
                        <td className="py-3 px-4 text-[#475569] text-sm">{app.programmeName}</td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">{app.intake}</Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={statusConfig[app.status]?.color || 'bg-gray-100'}>
                            {app.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-[#475569] text-center py-8">No applications yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
